import styled from "@emotion/styled"
import { PAccordion, PHeading, PInlineNotification } from "@porsche-design-system/components-react"
import { spacingStaticMd, spacingStaticXl } from "@porsche-design-system/components-react/emotion"
import type { DatePrice, PriceBreakdownViewState } from "@slfinrtl/ui-bff-routes"
import { ShowLuxuryTaxType, vehicleTypeMessages } from "@slfinrtl/ui-bff-routes"
import { useState } from "react"
import { useFormContext } from "react-hook-form"
import { defineMessages, FormattedMessage, useIntl } from "react-intl"
import { Flex } from "src/components/Flex"
import { CheckboxGroupField, FormLayout, InputField } from "src/components/form"
import { RadioGroupField } from "src/components/form/radio/radioGroup/RadioGroupField"
import { GridItem } from "src/components/grid/GridItem"
import type { InventoryInformationFormValues } from "src/components/inventoryInformation/inventoryAndIntakeForm"
import { Checkbox } from "../../../../form/checkbox/Checkbox"
import { gridSize } from "../../../common/gridSize.const"
import { PricingFormGrid } from "../../components/PricingFormGrid"
import { PriceHistory } from "../../components/priceHistory/PriceHistory"
import { pricingFieldMessages } from "../../hooks/pricingField.messages"
import type { CanadaPriceBreakdownPricingFields } from "../canadaPriceBreakdownPricing.fields"
import { PriceBreakdown } from "./priceBreakdown/PriceBreakdown"
import { priceBreakdownMessages } from "./priceBreakdown/priceBreakdown.messages"

const messages = defineMessages({
	luxuryTaxDisclaimer: {
		defaultMessage:
			"It is the responsibility of the individual Porsche Centre to display the " +
			"luxury tax amount for all applicable vehicle listings in Porsche Finder.",
		id: "listingForm.luxuryTaxDisclaimer",
	},
})

interface CanadaPriceBreakdownPricingFormProps {
	error?: string
	fields: CanadaPriceBreakdownPricingFields
	priceBreakdown: PriceBreakdownViewState | undefined
	priceHistory: DatePrice[] | undefined
}

export const CanadaPriceBreakdownPricingForm = ({
	error,
	fields,
	priceBreakdown,
	priceHistory,
}: CanadaPriceBreakdownPricingFormProps) => {
	const { formatMessage } = useIntl()
	const [isAccessoriesExpanded, setIsAccessoriesExpanded] = useState(false)

	const { watch } = useFormContext<InventoryInformationFormValues>()

	return (
		<PricingFormGrid>
			<GridItemFormLayout size={gridSize}>
				<InputField {...fields.minimumPrice} />
				<InputField {...fields.purchasePrice} />
				{fields.vehiclePrice && <InputField {...fields.vehiclePrice} />}
				<Checkbox
					checked
					disabledHint={formatMessage(pricingFieldMessages.notPossibleToEditVatDeductible, {
						vehicleType: formatMessage(vehicleTypeMessages.NEW_CAR),
					})}
				>
					<FormattedMessage {...pricingFieldMessages.vatDeductibleLabel} />
				</Checkbox>
				{fields.hasSpareWheel && <RadioGroupField {...fields.hasSpareWheel} />}
				{fields.showLuxuryTax && <RadioGroupField {...fields.showLuxuryTax} />}
				{watch("showLuxuryTax") === ShowLuxuryTaxType.NO && (
					<StyledInlineNotification state="warning" dismissButton={false}>
						<FormattedMessage {...messages.luxuryTaxDisclaimer} />
					</StyledInlineNotification>
				)}
				{fields.accessories && (
					<PAccordion open={isAccessoriesExpanded} onUpdate={() => setIsAccessoriesExpanded((prev) => !prev)}>
						<PHeading slot="summary" tag="h2" size="sm" weight="semibold">
							<FormattedMessage {...pricingFieldMessages.accessories} />
						</PHeading>
						<CheckboxGroupField {...fields.accessories} />
					</PAccordion>
				)}
			</GridItemFormLayout>

			<PriceHistoryContainer size={gridSize} $direction="column">
				{priceHistory && priceHistory.length > 0 && <PriceHistory view={priceHistory} />}
				{priceBreakdown && (
					<div>
						<PHeading size="md">
							<FormattedMessage {...priceBreakdownMessages.title} />
						</PHeading>
						{priceBreakdown.state === "error" ? (
							<StyledInlineNotification state="error" dismissButton={false} actionIcon="exclamation">
								{error ?? <FormattedMessage {...priceBreakdownMessages.genericError} />}
							</StyledInlineNotification>
						) : priceBreakdown.state === "loading" ? (
							<PriceBreakdown
								view={priceBreakdown.lastPriceBreakdown}
								skeletonPrices
								collapsible={false}
							/>
						) : (
							<PriceBreakdown view={priceBreakdown.priceBreakdown} collapsible={false} />
						)}
					</div>
				)}
			</PriceHistoryContainer>
		</PricingFormGrid>
	)
}

const StyledFlex = styled(Flex)`
	gap: ${spacingStaticXl};
`

const StyledFormLayout = styled(FormLayout)`
	align-self: start; /* needed when price history is larger than form, otherwise gaps between inputs increase */
`

const GridItemFormLayout = StyledFormLayout.withComponent(GridItem)
const PriceHistoryContainer = StyledFlex.withComponent(GridItem)

const StyledInlineNotification = styled(PInlineNotification)`
	margin-top: ${spacingStaticMd};
`
